import type { CameraRoute } from '@datum-sdk/scene-format';

/**
 * The 1890s preloader flights, as SDK scene data: the tour, then a flyover
 * that climbs off `previewTourEnd`, rings the Kaaba once above the cage and
 * descends back onto the same pose — its last view and the rest pose.
 *
 * Converted once from the legacy `kaabaOttomanSceneConfig.camera` by
 * `convertLegacyPreloader.ts` (which says what in here is load-bearing); the
 * routes are the source since. Tune them in the SDK editor (`?flight` → Routes →
 * Copy Routes) — the same workflow as `kaabaRoutes.ts` — and
 * `sceneRoutes.test.ts` holds them to what the preloader and the scene need.
 *
 * Both rings — the tour's and the flyover's — fly outside the camera cage, so
 * the preloader lifts collisions for its routes (`createSdkScene.ts`). The
 * full turn is 18 views (CR C13), at about a frame per knot (CR C14).
 */
export const KAABA_OTTOMAN_CAMERA_ROUTES: CameraRoute[] = [
  {
    id: 'preview-enter',
    waypoints: [
      {
        target: [-0.18, 1.46, 4.59],
        lookAt: [0.01, -0.45, 0.13],
        duration: 1000,
        easing: [0.65, 0, 0.35, 1],
      },
      {
        orbit: {
          radius: 4.855491736,
          azimuth: 1.796631052,
          polar: 1.166503155,
        },
        target: [0.01, -0.45, 0.13],
        lookAt: 'target',
        duration: 500,
        easing: [0.333333333, 0, 0.666666667, 0.333333333],
      },
    ],
  },
  {
    id: 'preview-turntable',
    loop: true,
    waypoints: [
      {
        orbit: {
          radius: 4.855491736,
          azimuth: 3.367427379,
          polar: 1.166503155,
        },
        target: [0.01, -0.45, 0.13],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
      {
        orbit: {
          radius: 4.855491736,
          azimuth: 4.938223706,
          polar: 1.166503155,
        },
        target: [0.01, -0.45, 0.13],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
      {
        orbit: {
          radius: 4.855491736,
          azimuth: 6.509020032,
          polar: 1.166503155,
        },
        target: [0.01, -0.45, 0.13],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
      {
        orbit: {
          radius: 4.855491736,
          azimuth: 8.079816359,
          polar: 1.166503155,
        },
        target: [0.01, -0.45, 0.13],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
    ],
  },
  {
    id: 'preview-exit',
    waypoints: [
      {
        target: [-0.75, 0.62, 2.92],
        lookAt: [0.01, -0.45, 0.13],
        duration: 1000,
        easing: [0.65, 0, 0.35, 1],
      },
    ],
  },
  {
    id: 'preloader-flyover',
    waypoints: [
      {
        duration: 1000,
      },
      {
        kind: 'view',
        position: [-0.75, 2.355, 2.92],
        lookAt: [0.01, -0.45, 0.13],
        duration: 2000,
        easing: [0.333333333, 0, 0.666666667, 0.245515318],
      },
      {
        kind: 'view',
        position: [-1.658598518, 2.355, 2.492084458],
        lookAt: [0.01, -0.45, 0.13],
        duration: 503.608718235,
        easing: [0.333333333, 0.324853014, 0.666666667, 0.659093652],
      },
      {
        kind: 'view',
        position: [-2.365850179, 2.355, 1.778939031],
        lookAt: [0.01, -0.45, 0.13],
        duration: 472.191581101,
        easing: [0.333333333, 0.326888228, 0.666666667, 0.660557613],
      },
      {
        kind: 'view',
        position: [-2.786539245, 2.355, 0.86690722],
        lookAt: [0.01, -0.45, 0.13],
        duration: 456.479702809,
        easing: [0.333333333, 0.328149089, 0.666666667, 0.661789243],
      },
      {
        kind: 'view',
        position: [-2.869924405, 2.355, -0.134006476],
        lookAt: [0.01, -0.45, 0.13],
        duration: 444.30748907,
        easing: [0.333333333, 0.329192232, 0.666666667, 0.662811063],
      },
      {
        kind: 'view',
        position: [-2.605948179, 2.355, -1.103077095],
        lookAt: [0.01, -0.45, 0.13],
        duration: 434.946644869,
        easing: [0.333333333, 0.330084908, 0.666666667, 0.663688117],
      },
      {
        kind: 'view',
        position: [-2.026449996, 2.355, -1.923420419],
        lookAt: [0.01, -0.45, 0.13],
        duration: 427.912129229,
        easing: [0.333333333, 0.330872616, 0.666666667, 0.664464517],
      },
      {
        kind: 'view',
        position: [-1.201325888, 2.355, -2.496090934],
        lookAt: [0.01, -0.45, 0.13],
        duration: 422.877819659,
        easing: [0.333333333, 0.331587968, 0.666666667, 0.665172003],
      },
      {
        kind: 'view',
        position: [-0.230098, 2.355, -2.752016126],
        lookAt: [0.01, -0.45, 0.13],
        duration: 419.629190145,
        easing: [0.333333333, 0.332255784, 0.666666667, 0.665834841],
      },
      {
        kind: 'view',
        position: [0.770089249, 2.355, -2.660327639],
        lookAt: [0.01, -0.45, 0.13],
        duration: 418.035753346,
        easing: [0.333333333, 0.332896251, 0.666666667, 0.666472894],
      },
      {
        kind: 'view',
        position: [1.678598518, 2.355, -2.232084458],
        lookAt: [0.01, -0.45, 0.13],
        duration: 418.035710747,
        easing: [0.333333333, 0.333527072, 0.666666667, 0.667103715],
      },
      {
        kind: 'view',
        position: [2.385850179, 2.355, -1.518939031],
        lookAt: [0.01, -0.45, 0.13],
        duration: 419.629060715,
        easing: [0.333333333, 0.334165124, 0.666666667, 0.667744182],
      },
      {
        kind: 'view',
        position: [2.806539245, 2.355, -0.60690722],
        lookAt: [0.01, -0.45, 0.13],
        duration: 422.87759832,
        easing: [0.333333333, 0.33482796, 0.666666667, 0.668411996],
      },
      {
        kind: 'view',
        position: [2.889924405, 2.355, 0.394006476],
        lookAt: [0.01, -0.45, 0.13],
        duration: 427.911806881,
        easing: [0.333333333, 0.335535443, 0.666666667, 0.669127344],
      },
      {
        kind: 'view',
        position: [2.625948179, 2.355, 1.363077095],
        lookAt: [0.01, -0.45, 0.13],
        duration: 434.946207284,
        easing: [0.333333333, 0.336311839, 0.666666667, 0.669915047],
      },
      {
        kind: 'view',
        position: [2.046449996, 2.355, 2.183420419],
        lookAt: [0.01, -0.45, 0.13],
        duration: 444.306914968,
        easing: [0.333333333, 0.337188886, 0.666666667, 0.670807717],
      },
      {
        kind: 'view',
        position: [1.221325888, 2.355, 2.756090934],
        lookAt: [0.01, -0.45, 0.13],
        duration: 456.478960559,
        easing: [0.333333333, 0.338210697, 0.666666667, 0.67185085],
      },
      {
        kind: 'view',
        position: [0.250098, 2.355, 3.012016126],
        lookAt: [0.01, -0.45, 0.13],
        duration: 472.188502369,
        easing: [0.333333333, 0.339442272, 0.666666667, 0.673111676],
      },
      {
        kind: 'view',
        position: [-0.750089249, 2.355, 2.920327639],
        lookAt: [0.01, -0.45, 0.13],
        duration: 503.636209693,
        easing: [0.333333333, 0.340906775, 0.666666667, 0.675147355],
      },
      {
        kind: 'view',
        position: [-0.75, 0.62, 2.92],
        lookAt: [0.01, -0.45, 0.13],
        duration: 2000,
        easing: [0.333333333, 0.754484214, 0.666666667, 1],
      },
    ],
  },
];
